import {
  HelicopterIcon,
  MarkerIcon,
  ClockIcon,
  PhoneIcon,
} from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import type { EtlTransportResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { extractTransportAddress } from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatTransportStatuses } from '../../../../AirAmbulanceForm/etl/etlStatusUtils';
import useAirAmbulanceEtlEta from '../../../../AirAmbulanceForm/etl/hooks/useAirAmbulanceEtlEta';
import {
  StyledTransportListItem,
  NotificationBubble,
  StatusBadge,
  TransportIconWrapper,
  TransportDetails,
  DetailRow,
} from './TransportListItem.styles';

interface TransportListItemProps {
  transport: EtlTransportResponse;
  isUnread: boolean;
  onClick: () => void;
}

function TransportListItem({
  transport,
  isUnread,
  onClick,
}: TransportListItemProps) {
  const formattedStatuses = formatTransportStatuses(transport.statuses);
  const latestStatus = formattedStatuses[0];
  const address = extractTransportAddress(transport.verbatim_message);
  const fontWeight = isUnread ? ('bold' as const) : undefined;
  const textColor = isUnread
    ? ('primaryText' as const)
    : ('secondaryText' as const);

  const eta = useAirAmbulanceEtlEta();

  return (
    <StyledTransportListItem onClick={onClick} data-name="transport-list-item">
      <TransportIconWrapper>
        <HelicopterIcon color="primaryText" height={20} width={20} />
        {isUnread && <NotificationBubble />}
      </TransportIconWrapper>

      <TransportDetails>
        <Text
          color="primaryText"
          fontWeight={fontWeight}
          size="large"
          lineHeight="1.2"
        >
          Air Ambulance
        </Text>

        {latestStatus?.display && (
          <StatusBadge>{latestStatus.display}</StatusBadge>
        )}

        {address && (
          <DetailRow>
            <MarkerIcon color="secondaryText" height={12} width={12} />
            <Text color={textColor} fontWeight={fontWeight} size="small">
              {address}
            </Text>
          </DetailRow>
        )}

        {eta?.display && (
          <DetailRow>
            <ClockIcon color="secondaryText" height={12} width={12} />
            <Text color={textColor} fontWeight={fontWeight} size="small">
              {eta.display}
            </Text>
          </DetailRow>
        )}

        {transport.caller_id && (
          <DetailRow>
            <PhoneIcon color="secondaryText" height={12} width={12} />
            <Text color={textColor} fontWeight={fontWeight} size="small">
              Ordered for: {transport.caller_id}
            </Text>
          </DetailRow>
        )}
      </TransportDetails>
    </StyledTransportListItem>
  );
}

export default TransportListItem;
